import { useMemo } from 'react'

import { enumCodec, QueryCodec, QuerySchema, QuerySetter, useQuerySettings } from '@/hooks/useQueryState'

import {
  DEFAULT_PDF_SETTINGS,
  PdfSettings,
  ScrollMode,
  SpecialZoomLevel,
  TextDirection,
  ViewMode,
  ZoomLevel,
} from '../pdf/components/pdf-settings'

// 数値ズームの許容範囲（pdf-document のピンチズーム制約と同じ）
const MIN_ZOOM = 0.3
const MAX_ZOOM = 5.0

const SPECIAL_ZOOM_LEVELS = Object.values(SpecialZoomLevel)

/** 'page-width' などのプリセットと数値ズームの両方を扱うコーデック */
const zoomCodec = (defaultValue: ZoomLevel): QueryCodec<ZoomLevel> => ({
  parse: (raw) => {
    if (!raw) return defaultValue

    const preset = SPECIAL_ZOOM_LEVELS.find((value) => value === raw)
    if (preset) return preset

    const numeric = Number(raw)
    if (!Number.isFinite(numeric)) return defaultValue
    return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, numeric))
  },
  // ピンチズームの端数で URL が汚れないよう小数 3 桁に丸める
  serialize: (value) => (typeof value === 'number' ? String(Math.round(value * 1000) / 1000) : value),
  defaultValue,
})

const buildSchema = (defaults: PdfSettings): QuerySchema<PdfSettings> => ({
  scrollMode: { param: 'scroll', codec: enumCodec(Object.values(ScrollMode), defaults.scrollMode) },
  viewMode: { param: 'view', codec: enumCodec(Object.values(ViewMode), defaults.viewMode) },
  zoom: { param: 'zoom', codec: zoomCodec(defaults.zoom) },
  direction: { param: 'dir', codec: enumCodec(Object.values(TextDirection), defaults.direction) },
  dragMode: { param: 'drag', codec: enumCodec(['free', 'snap'], defaults.dragMode) },
})

/**
 * PDF ビューアの表示設定を URL クエリパラメータで管理するフック。
 *
 * リロードや URL 共有で表示設定（スクロールモード / ページ配置 / ズーム / ページ方向 / 送り方）を復元できる。
 * URL に無い項目は defaults（作品ごとに覚えた設定など）を使う。defaults は再レンダーで作り直さないこと。
 */
export const usePdfSettingsQuery = (
  defaults: PdfSettings = DEFAULT_PDF_SETTINGS,
): [PdfSettings, QuerySetter<PdfSettings>] => {
  const schema = useMemo(() => buildSchema(defaults), [defaults])
  return useQuerySettings(schema)
}
